import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { SocialLogoKey } from '../components/symphony/ui/SocialLogo';

export const SOCIAL_POST_GRID_TILES = 4;
export const SOCIAL_POST_CAPTION_LINES = 5;
const CAPTION_CLAMP_CHARS = 280;
const PORTRAIT_BELOW = 0.9;
const PORTRAIT_NARROWEST = 9 / 16;
const LANDSCAPE_NARROWEST = 4 / 3;
const LANDSCAPE_WIDEST = 1.91;
const UNMEASURED_ASPECT = 16 / 9;
const HIGHLIGHTED = /(#[\p{L}\p{M}\p{N}_]+|https?:\/\/\S+|www\.\S+)/gu;

const LOGO_KEYS: Record<SocialLogoKey, true> = {
  INSTAGRAM: true,
  TIKTOK: true,
  FACEBOOK: true,
  LINKEDIN: true,
  X: true,
  YOUTUBE: true,
  PINTEREST: true,
  THREADS: true,
  YELP: true,
  ETSY: true,
  GOOGLE_BUSINESS: true,
};

export interface SocialPostMediaLayout {
  tiles: SocialPostPreviewMedia[];
  more: number;
  showsNotShown: boolean;
}

export function socialPostMediaLayout(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount'>): SocialPostMediaLayout {
  const tiles = card.media.slice(0, SOCIAL_POST_GRID_TILES);
  const full = tiles.length === SOCIAL_POST_GRID_TILES;
  return {
    tiles,
    more: full ? Math.max(0, card.mediaCount - SOCIAL_POST_GRID_TILES) : 0,
    showsNotShown: !full && card.mediaCount > card.media.length,
  };
}

export interface SocialPostSingleFrame {
  portrait: boolean;
  aspectRatio: number;
}

const PORTRAIT_POST_TYPES: readonly (SocialPostPreviewCardMetadata['postType'] | undefined)[] = ['reel', 'story', 'short'];

export function socialPostVideoAspect(postType: SocialPostPreviewCardMetadata['postType']): number {
  return PORTRAIT_POST_TYPES.includes(postType) ? PORTRAIT_NARROWEST : UNMEASURED_ASPECT;
}

export function socialPostSingleFrame(aspect: number | undefined): SocialPostSingleFrame {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) {
    return { portrait: false, aspectRatio: UNMEASURED_ASPECT };
  }
  if (aspect < PORTRAIT_BELOW) {
    return { portrait: true, aspectRatio: Math.max(aspect, PORTRAIT_NARROWEST) };
  }
  return { portrait: false, aspectRatio: Math.min(Math.max(aspect, LANDSCAPE_NARROWEST), LANDSCAPE_WIDEST) };
}

export interface SocialPostHeader {
  title: string;
  meta?: string;
}

export function socialPostHeader(card: SocialPostPreviewCardMetadata): SocialPostHeader {
  const title = card.accountName ?? card.accountLabel ?? card.networkLabel;
  const meta = [title === card.networkLabel ? undefined : card.networkLabel, card.labels.postType].filter(Boolean).join(' · ');
  return meta ? { title, meta } : { title };
}

export interface SocialPostCaptionSegment {
  text: string;
  highlighted: boolean;
}

export function socialPostCaptionSegments(caption: string): SocialPostCaptionSegment[] {
  const segments: SocialPostCaptionSegment[] = [];
  let last = 0;
  for (const match of caption.matchAll(HIGHLIGHTED)) {
    const start = match.index ?? 0;
    if (start > last) {
      segments.push({ text: caption.slice(last, start), highlighted: false });
    }
    segments.push({ text: match[0], highlighted: true });
    last = start + match[0].length;
  }
  if (last < caption.length) {
    segments.push({ text: caption.slice(last), highlighted: false });
  }
  return segments;
}

export function socialPostLogoKey(network: string): string {
  return network.trim().toUpperCase();
}

export function socialPostHasLogo(network: string): boolean {
  return socialPostLogoKey(network) in LOGO_KEYS;
}

export function socialPostLinkHost(link: string): string | undefined {
  try {
    const host = new URL(link).hostname;
    return host || undefined;
  } catch {
    return undefined;
  }
}

export function socialPostCaptionIsLong(caption: string): boolean {
  return caption.split('\n').length > SOCIAL_POST_CAPTION_LINES || caption.length > CAPTION_CLAMP_CHARS;
}

export function formatSocialPostSchedule(scheduledAt: string, locale: string | undefined): string | undefined {
  const at = new Date(scheduledAt);
  if (Number.isNaN(at.getTime())) {
    return undefined;
  }
  try {
    return new Intl.DateTimeFormat(locale || undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
  } catch {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
  }
}

export function socialPostScheduleLine(card: SocialPostPreviewCardMetadata, locale: string | undefined): string {
  const when = (card.scheduledAt && formatSocialPostSchedule(card.scheduledAt, locale)) || card.scheduledText;
  return when ? `${card.labels.scheduled} ${when}` : card.labels.postsNow;
}

function lastPathSegment(url: string): string | undefined {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : undefined;
  } catch {
    return undefined;
  }
}

export function socialPostMediaName(media: SocialPostPreviewMedia, index: number): string {
  return lastPathSegment(media.url) ?? `${media.kind}-${index + 1}`;
}

const WIX_MEDIA_HOST = 'static.wixstatic.com';
const WIX_MEDIA_HOST_SUFFIX = '.wixstatic.com';
const WIX_VARIANT_SUFFIX = /\/v[12]\/(fit|fill)\/[^/]+\/[^/]+$/;

export interface SocialPostRendition {
  mode: 'fill' | 'fit';
  width: number;
  height: number;
}

export const SOCIAL_POST_RENDITIONS: Record<'single' | 'two' | 'threeLead' | 'threeSide' | 'four', SocialPostRendition> = {
  single: { mode: 'fit', width: 896, height: 896 },
  two: { mode: 'fill', width: 448, height: 448 },
  threeLead: { mode: 'fill', width: 600, height: 600 },
  threeSide: { mode: 'fill', width: 300, height: 300 },
  four: { mode: 'fill', width: 448, height: 300 },
};

export function socialPostGridRendition(count: number, index: number): SocialPostRendition {
  if (count === 2) {
    return SOCIAL_POST_RENDITIONS.two;
  }
  if (count === 3) {
    return index === 0 ? SOCIAL_POST_RENDITIONS.threeLead : SOCIAL_POST_RENDITIONS.threeSide;
  }
  return SOCIAL_POST_RENDITIONS.four;
}

export function socialPostImageRendition(url: string, { mode, width, height }: SocialPostRendition): string {
  const widthPx = Math.round(width);
  const heightPx = Math.round(height);
  if (!url || widthPx <= 0 || heightPx <= 0 || url.includes('?') || url.includes('#')) {
    return url;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const host = parsed.hostname.toLowerCase();
  if (
    (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') ||
    (host !== WIX_MEDIA_HOST && !host.endsWith(WIX_MEDIA_HOST_SUFFIX)) ||
    !parsed.pathname.includes('/media/')
  ) {
    return url;
  }
  const basePath = parsed.pathname.replace(WIX_VARIANT_SUFFIX, '').replace(/\/+$/, '');
  return `${parsed.protocol}//${parsed.host}${basePath}/v1/${mode}/w_${widthPx},h_${heightPx},q_85/file.webp`;
}
